import { supabase } from './supabase'

/**
 * Detects if a URL is a temporary signed URL from Instagram, Threads, or Facebook.
 * These URLs typically contain 'fbcdn.net' and expire.
 */
export function isTemporaryUrl(url: string | null | undefined): boolean {
    if (!url) return false
    return url.includes('fbcdn.net') ||
        url.includes('instagram.com/p/') || // Fallback detection for direct links if scraper yields them
        url.includes('cdninstagram.com')
}

/**
 * Mirror an external image to Supabase Storage for permanent persistence.
 * Returns the public URL of the mirrored image.
 */
export async function mirrorImage(externalUrl: string, fileNamePrefix: string = 'pick'): Promise<string> {
    try {
        if (!isTemporaryUrl(externalUrl)) {
            return externalUrl
        }

        // 1. Fetch image content
        const response = await fetch(externalUrl)
        if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`)

        const blob = await response.blob()
        const contentType = blob.type || 'image/jpeg'
        const extension = contentType.split('/')[1] || 'jpg'

        // 2. Generate unique file name
        const timestamp = Date.now()
        const randomString = Math.random().toString(36).substring(7)
        const fileName = `${fileNamePrefix}-${timestamp}-${randomString}.${extension}`
        const filePath = `mirrored/${fileName}`

        let uploadData: Blob = blob
        let finalContentType = contentType

        // Optimize Pick images (max 1024px)
        if (contentType.startsWith('image/')) {
            try {
                uploadData = await resizeImage(blob, 1024, 0.8)
                finalContentType = 'image/jpeg'
            } catch (e) {
                console.error('Mirror resize failed:', e)
            }
        }

        // 3. Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
            .from('picks')
            .upload(filePath, uploadData, {
                contentType: finalContentType,
                cacheControl: '3600',
                upsert: false
            })

        if (uploadError) throw uploadError

        // 4. Get Public URL
        const { data: { publicUrl } } = supabase.storage
            .from('picks')
            .getPublicUrl(filePath)

        return publicUrl
    } catch (error) {
        console.error('Mirroring failed, falling back to original URL:', error)
        return externalUrl
    }
}

/**
 * Resize and compress an image file client-side.
 */
async function resizeImage(file: File | Blob, maxWidth: number = 1200, quality: number = 0.8): Promise<Blob> {
    return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            const img = new Image()
            img.onload = () => {
                const canvas = document.createElement('canvas')
                let width = img.width
                let height = img.height

                // Calculate scale
                if (width > maxWidth) {
                    height = Math.round((maxWidth / width) * height)
                    width = maxWidth
                }

                canvas.width = width
                canvas.height = height

                const ctx = canvas.getContext('2d')
                if (!ctx) {
                    resolve(file) // Fallback if no context
                    return
                }

                ctx.drawImage(img, 0, 0, width, height)

                canvas.toBlob(
                    (blob) => {
                        if (blob) resolve(blob)
                        else resolve(file) // Fallback to original
                    },
                    'image/jpeg',
                    quality
                )
            }
            img.onerror = () => resolve(file) // Fallback
            img.src = e.target?.result as string
        }
        reader.onerror = () => resolve(file) // Fallback
        reader.readAsDataURL(file)
    })
}

/**
 * Upload a file directly to Supabase Storage.
 * Returns the public URL of the uploaded image.
 */
export async function uploadImage(file: File, bucket: string = 'picks', fileNamePrefix: string = 'upload'): Promise<string> {
    let uploadData: File | Blob = file
    let contentType = file.type || 'image/jpeg'

    // Optimize images if they are reasonably large OR to ensure common size
    // Threshold: > 300KB or if it's an image
    if (file.type.startsWith('image/')) {
        try {
            // Avatars can be smaller, but 1200 is a safe upper bound for all (including covers)
            const maxWidth = fileNamePrefix === 'avatar' ? 600 : 1200
            uploadData = await resizeImage(file, maxWidth, 0.85)
            contentType = 'image/jpeg' // We convert to jpeg in resizeImage
        } catch (e) {
            console.error('Client-side resize failed:', e)
        }
    }

    const fileExt = contentType.split('/')[1] || 'jpg'
    const timestamp = Date.now()
    const randomStr = Math.random().toString(36).substring(7)
    const fileName = `${fileNamePrefix}-${timestamp}-${randomStr}.${fileExt}`

    // Path inside the bucket
    const filePath = `${fileNamePrefix}/${fileName}`

    const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, uploadData, {
            contentType,
            upsert: true,
            cacheControl: '3600'
        })

    if (uploadError) {
        console.error('Storage Upload Error:', uploadError)
        throw uploadError
    }

    const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath)

    return publicUrl
}
